import type { GscActivationFailureReason } from '../SearchPerformanceAnalyticsEvents';

// Hard bound: rest-api `@ArrayMaxSize(5)` and visibility's project cap.
export const GSC_MAX_SYNCED_DOMAINS = 5;

export const GSC_ACTIVATION_FAILURE_COPY: Record<Exclude<GscActivationFailureReason, 'domain_cap_reached'>, string> = {
  no_property_access:
    'Your Google account needs Owner or Full access in Search Console to sync one or more of the selected domains. Adjust your selection, or ask a Search Console owner to upgrade this account.',
  already_connected: 'These domains are already connected. Reload the page to see your current setup.',
  overlapping_selection:
    'Two of the selected properties describe the same domain. Keep one property per domain and try again.',
  domain_already_connected:
    'A selected domain is already being synced from another Search Console property. Adjust your selection, or replace the existing property when offered.',
  not_in_plan: 'Search Performance is not included in the current plan.',
  request_failed: 'Could not connect the selected domains. Please try again.',
};

export function describeGscActivationFailure(
  reason: GscActivationFailureReason,
  { maxDomains }: { maxDomains: number }
): string {
  if (reason !== 'domain_cap_reached') {
    return GSC_ACTIVATION_FAILURE_COPY[reason];
  }
  if (maxDomains === 1) {
    return "Your plan syncs one domain, and it's already in use. Remove it on the Integrations page before adding another, or upgrade to Enterprise to sync up to five.";
  }
  return `You've reached your plan's limit of ${maxDomains} synced domains. Disconnect a domain on the Integrations page before adding another.`;
}

export const GSC_SAME_DOMAIN_BLOCKED_COPY =
  'Only one Search Console property can sync per domain — another property you selected already covers this one.';

export const GSC_PICKER_CAP_HELP_AT_CAP = 'You have used every domain in your plan. Clear one to choose another.';

export type GscActivationConflict = {
  domain: string;
  existing_property_url: string;
  selected_property_url: string;
};

type GscActivationGuardBody = {
  error?: string;
  replaceable?: boolean;
  conflicts?: GscActivationConflict[];
  // rest-api's own plan cap (`plan-domain-cap-reached`), unlike the bare 422
  // visibility answers for its project-wide cap.
  max_domains?: number;
  live_domains?: number;
};

export function parseGscActivationGuardBody(err: unknown): GscActivationGuardBody | undefined {
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) {
    return undefined;
  }
  return data as GscActivationGuardBody;
}

export function toGscActivationFailureReason(
  httpStatus: number | undefined,
  guardCode: string | undefined
): GscActivationFailureReason {
  if (guardCode === 'overlapping-property-selection') {
    return 'overlapping_selection';
  }
  if (guardCode === 'domain-already-connected') {
    return 'domain_already_connected';
  }
  if (guardCode === 'plan-domain-cap-reached') {
    return 'domain_cap_reached';
  }
  if (guardCode === 'search-performance-not-in-plan') {
    return 'not_in_plan';
  }
  switch (httpStatus) {
    case 403:
      return 'no_property_access';
    case 409:
      return 'already_connected';
    case 422:
      return 'domain_cap_reached';
    default:
      return 'request_failed';
  }
}

export function httpStatusFromActivationError(err: unknown): number | undefined {
  if (typeof (err as { status?: unknown }).status === 'number') {
    return (err as { status: number }).status;
  }
  return (err as { originalStatus?: number }).originalStatus;
}

export const GSC_ACTIVATION_NONE_PAIRED_COPY = 'Could not connect the selected domains. Please try again.';

export const GSC_ACTIVATION_UNKNOWN_COPY =
  'We could not confirm the connection. Reload the page to check your synced domains, then try again if they are missing.';

// `failed[].message` is visibility's reason (a timeout, an axios error) — it
// goes to analytics and logs, never to the screen.
export function describeNonePairedActivation(failureCode: string | undefined): string {
  return failureCode === 'gsc-activation-unknown' ? GSC_ACTIVATION_UNKNOWN_COPY : GSC_ACTIVATION_NONE_PAIRED_COPY;
}

export function describePartialActivation(failedUrls: string[]): string {
  return `Connected, except ${failedUrls.join(', ')}. Add it again from Manage synced domains.`;
}

// nginx in front of rest-api cuts the request at 60s; RTK reports the cut as a
// 502/504 or as a FETCH_ERROR / TIMEOUT_ERROR with no numeric status.
export function isGatewayCut(err: unknown): boolean {
  const status = (err as { status?: unknown }).status;
  if (status === 502 || status === 504) {
    return true;
  }
  return status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR';
}
